/**
 * Estate overview — one horizontal stacked bar per provider, segmented by
 * status (up / degraded / down / unknown). Replaces the cost graph with
 * something operationally useful at a glance.
 */
import { useMemo } from 'react'

interface AppRow {
  app_group: string
  last_status: string | null
  civo_status?: string | null
  civo_resource_type?: string | null
}
interface Props { apps: AppRow[] | null }

const COLORS: Record<string, string> = {
  up:       '#3ddc84',
  degraded: '#ffb454',
  down:     '#ff6b81',
  unknown:  '#64748b',
}
const ORDER: Array<keyof typeof COLORS> = ['up', 'degraded', 'down', 'unknown']

export default function EstateOverview({ apps }: Props) {
  interface GroupRow { group: string; up: number; degraded: number; down: number; unknown: number; total: number }
  const rows = useMemo<GroupRow[]>(() => {
    if (!apps) return []
    // Provider-managed rows (Hetzner/Hostinger/Railway/Vercel) skip HTTP
    // probing so last_status is null. Use civo_status (provider API state)
    // for those — ACTIVE → up, STOPPED → down, anything else → degraded.
    const PROVIDER_MANAGED = new Set(['hetzner_server','hostinger_vps','railway_service','vercel_project'])
    function effectiveStatus(a: AppRow): string {
      if (a.last_status) return a.last_status
      if (PROVIDER_MANAGED.has(a.civo_resource_type ?? '')) {
        if (a.civo_status === 'ACTIVE') return 'up'
        if (a.civo_status === 'STOPPED') return 'down'
        if (a.civo_status === 'BUILDING' || a.civo_status === 'STOPPING') return 'degraded'
      }
      return 'unknown'
    }
    const m = new Map<string, Record<string, number>>()
    for (const a of apps) {
      const s = effectiveStatus(a)
      if (!m.has(a.app_group)) m.set(a.app_group, { up: 0, degraded: 0, down: 0, unknown: 0 })
      const counts = m.get(a.app_group)!
      counts[s] = (counts[s] ?? 0) + 1
    }
    // Sort by total desc, then alphabetical
    return Array.from(m.entries())
      .map(([group, c]) => ({
        group,
        up:       c.up       ?? 0,
        degraded: c.degraded ?? 0,
        down:     c.down     ?? 0,
        unknown:  c.unknown  ?? 0,
        total:    ORDER.reduce((s, k) => s + (c[k] ?? 0), 0),
      }))
      .sort((a, b) => b.total - a.total || a.group.localeCompare(b.group))
  }, [apps])

  if (!apps) {
    return <div style={{ padding: '0.6rem', color: 'var(--text-muted)', fontSize: '0.78rem' }}>Loading estate…</div>
  }
  if (rows.length === 0) {
    return <div style={{ padding: '0.6rem', color: 'var(--text-muted)', fontSize: '0.78rem' }}>No infrastructure rows yet.</div>
  }

  const total = rows.reduce((s, r) => s + r.total, 0)
  const totalUp = rows.reduce((s, r) => s + (r.up ?? 0), 0)
  const totalDown = rows.reduce((s, r) => s + (r.down ?? 0), 0)
  const totalDeg  = rows.reduce((s, r) => s + (r.degraded ?? 0), 0)
  const healthPct = total ? Math.round((totalUp / total) * 100) : 0

  return (
    <div className="estate-overview">
      <div className="estate-summary">
        <span className="estate-summary-num">{healthPct}%</span>
        <span className="estate-summary-lab">healthy</span>
        <span className="estate-summary-meta">
          <span style={{ color: COLORS.up }}>{totalUp} up</span>
          {totalDeg > 0 && <span style={{ color: COLORS.degraded }}> · {totalDeg} degraded</span>}
          {totalDown > 0 && <span style={{ color: COLORS.down }}> · {totalDown} down</span>}
          <span style={{ color: 'var(--text-muted)' }}> / {total} total</span>
        </span>
      </div>
      <ul className="estate-rows">
        {rows.map(r => (
          <li key={r.group} className="estate-row">
            <span className="estate-row-name">{r.group}</span>
            <span className="estate-row-bar">
              {ORDER.map(s => {
                const v = (r as unknown as Record<string, number>)[s] ?? 0
                if (v === 0) return null
                const pct = (v / r.total) * 100
                return (
                  <span key={s} className="estate-row-seg"
                    style={{ width: `${pct}%`, background: COLORS[s] }}
                    title={`${v} ${s}`} />
                )
              })}
            </span>
            <span className="estate-row-num">
              <span style={{ color: r.up === r.total ? COLORS.up : (r.down > 0 ? COLORS.down : COLORS.degraded) }}>
                {r.up}
              </span>
              <span style={{ color: 'var(--text-muted)' }}>/{r.total}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
